/**
 * docsModel — pure derivations over a Docs (TipTap) document.
 *
 * No Firebase, no TipTap import: these walk the plain JSON structure, so they
 * are unit-testable and reusable server-side later. DocsService owns the I/O.
 * (Same logic/IO split as songNotesModel.js vs SongNotesService.js.)
 *
 * Every field derived here is DENORMALIZED onto the doc on save:
 *   - plainText  → list previews + search without loading/parsing content
 *   - title      → the doc's H1, mirrored so a list query needn't parse content
 *   - mentions[] → "which docs reference this student/project/…" for free later
 */

export const ENTITY_TYPES = ['student', 'project', 'repertoire', 'task', 'doc'];

/* Walk every node in a TipTap doc, depth-first. */
function walk(node, visit) {
	if (!node || typeof node !== 'object') return;
	visit(node);
	if (Array.isArray(node.content)) node.content.forEach((child) => walk(child, visit));
}

/**
 * Flatten a doc to plain text. Block nodes are separated by newlines so words
 * from adjacent blocks don't run together and corrupt a search match.
 */
export function derivePlainText(json) {
	if (!json) return '';
	const parts = [];
	walk(json, (node) => {
		if (node.type === 'text' && typeof node.text === 'string') {
			parts.push(node.text);
		} else if (node.type === 'entityMention' && node.attrs?.label) {
			// A mention has no text child — without this its label is invisible
			// to search, so typing a student's name wouldn't find their doc.
			parts.push(`@${node.attrs.label}`);
		} else if (node.type === 'hardBreak') {
			parts.push('\n');
		}
	});
	// Insert block boundaries: rebuild by joining top-level blocks with newlines.
	const blocks = Array.isArray(json.content) ? json.content : [];
	if (blocks.length > 1) {
		return blocks
			.map((b) => {
				const seg = [];
				walk(b, (node) => {
					// Table cells are block boundaries too. Without this, a row's cells
					// concatenate ("Cue #PageDept") — which both hides real matches and
					// invents false ones spanning a cell edge.
					if (node.type === 'tableCell' || node.type === 'tableHeader') seg.push(' ');
					else if (node.type === 'text' && typeof node.text === 'string')
						seg.push(node.text);
					else if (node.type === 'entityMention' && node.attrs?.label)
						seg.push(`@${node.attrs.label}`);
				});
				return seg.join('').replace(/[ \t]+/g, ' ').trim();
			})
			.filter(Boolean)
			.join('\n')
			.trim();
	}
	return parts.join('').trim();
}

/**
 * The doc's title = its first H1. Falls back to the first non-empty text of any
 * kind, then to null — the caller decides how to render an untitled doc, so the
 * string 'Untitled' is never persisted as if the user typed it.
 */
export function deriveTitle(json) {
	if (!json) return null;
	let title = null;
	let firstText = null;

	walk(json, (node) => {
		if (title) return;
		if (node.type === 'heading' && node.attrs?.level === 1) {
			const seg = [];
			walk(node, (n) => {
				if (n.type === 'text' && typeof n.text === 'string') seg.push(n.text);
			});
			const text = seg.join('').trim();
			if (text) title = text;
		} else if (!firstText && node.type === 'text' && node.text?.trim()) {
			firstText = node.text.trim();
		}
	});

	return title || firstText || null;
}

/**
 * Collect entity mentions, deduped by (type, id) and order-stable. Malformed
 * mentions (missing id or an unknown type) are dropped rather than persisted —
 * a junk row here would otherwise poison a future "docs referencing X" query.
 */
export function extractMentions(json) {
	if (!json) return [];
	const seen = new Set();
	const out = [];

	walk(json, (node) => {
		if (node.type !== 'entityMention') return;
		const { entityType, id, label } = node.attrs || {};
		if (!id || !ENTITY_TYPES.includes(entityType)) return;
		const key = `${entityType}:${id}`;
		if (seen.has(key)) return;
		seen.add(key);
		out.push({ type: entityType, id, label: label || '' });
	});

	return out;
}

/** The starting document for a new doc: an empty H1, focused, ready to title. */
export function emptyDocContent() {
	return {
		type: 'doc',
		content: [
			{ type: 'heading', attrs: { level: 1 } },
			{ type: 'paragraph' },
		],
	};
}

/** Everything the save path denormalizes, in one call. */
export function deriveDocFields(json) {
	return {
		title: deriveTitle(json),
		plainText: derivePlainText(json),
		mentions: extractMentions(json),
	};
}
